import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { pushSchedule } from '@nexus/workspace';
import { useStore } from '../db/context';
import { cycleText } from '../notifications/scheduler';
import { loadNotificationSettings } from '../notifications/settings';
import { supabase, useSyncState } from '../sync/useSync';
import { refreshPush } from './push';

const CHANGE_DELAY_MS = 5_000;
const INTERVAL_MS = 15 * 60_000;

/**
 * While signed in, publish the coming week's reminder moments to the cloud, so
 * Web Push can deliver them when no Nexus is open. Every signed-in device does
 * this (desktop too); the schedule is only uploaded when it changed.
 */
export function usePushSchedule() {
  const store = useStore();
  const { t, i18n } = useTranslation();
  const userId = useSyncState((s) => s.session?.user.id ?? null);

  useEffect(() => {
    if (!userId) return;
    const sb = supabase();
    const memo = `nexus-push-schedule:${userId}`;
    let disposed = false;
    let running = false;

    const publish = async () => {
      if (disposed || running || !navigator.onLine) return;
      running = true;
      try {
        const settings = await loadNotificationSettings(store);
        const items = await pushSchedule(store, settings, cycleText(t, i18n.language));
        const fingerprint = JSON.stringify(items);
        let last: string | null = null;
        try {
          last = localStorage.getItem(memo);
        } catch {
          /* storage unavailable: just upload */
        }
        if (fingerprint === last) return;
        const { error } = await sb.rpc('replace_push_schedule', { items });
        if (error) throw new Error(error.message);
        try {
          localStorage.setItem(memo, fingerprint);
        } catch {
          /* ignore */
        }
      } catch (e) {
        console.warn('push schedule', e);
      } finally {
        running = false;
      }
    };

    void refreshPush(sb, store.deviceId).catch(() => {});
    void publish();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const soon = () => {
      clearTimeout(timer);
      timer = setTimeout(() => void publish(), CHANGE_DELAY_MS);
    };
    const off = store.subscribe(soon, ['tasks', 'notifications']);
    const interval = setInterval(() => void publish(), INTERVAL_MS);
    window.addEventListener('online', soon);
    return () => {
      disposed = true;
      off();
      clearTimeout(timer);
      clearInterval(interval);
      window.removeEventListener('online', soon);
    };
  }, [store, userId, t, i18n.language]);
}
